import { useCallback, useEffect, useRef, useState } from 'react'
import {
  type OnboardingStateResponse,
  type UpdateOnboardingDto,
} from '@super/shared-types'
import { onboardingClient } from '@/lib/api/onboarding.client'

interface UseOnboardingStateResult {
  state: OnboardingStateResponse | null
  isLoading: boolean
  error: Error | null
  patch: (input: UpdateOnboardingDto) => Promise<OnboardingStateResponse>
  refetch: () => Promise<void>
}

export function useOnboardingState(): UseOnboardingStateResult {
  const [state, setState] = useState<OnboardingStateResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const mountedRef = useRef(true)

  const load = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const next = await onboardingClient.getState()
      if (!mountedRef.current) return
      setState(next)
    } catch (err) {
      if (!mountedRef.current) return
      setError(err instanceof Error ? err : new Error(String(err)))
    } finally {
      if (mountedRef.current) setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    mountedRef.current = true
    load()
    return () => {
      mountedRef.current = false
    }
  }, [load])

  const patch = useCallback(async (input: UpdateOnboardingDto) => {
    const next = await onboardingClient.patch(input)
    if (mountedRef.current) setState(next)
    return next
  }, [])

  return { state, isLoading, error, patch, refetch: load }
}
